import { useMemo } from 'react';
import { AxisBottom, AxisLeft, Gridlines, labelStride } from './Axis';
import { categoryKeys, categoryLabels, longestLabelLength } from './categories';
import { ChartFrame } from './ChartFrame';
import { CursorLayer } from './CursorLayer';
import { type StackSegment, barRect, extentOf, stackFor, stackIsSound } from './geometry';
import {
  type BandScale,
  type LinearScale,
  bandScale,
  linearScale,
  niceDomain,
  tickCountFor,
} from './scale';
import { seriesColor, seriesHatch } from './series';
import type { ChartMargin, Formatter, PlotBox, Series } from './types';
import { type ChartCursor, useChartCursor } from './useChartCursor';

interface BarChartProps {
  title: string;
  summary?: string | undefined;
  series: readonly Series[];
  /** Horizontal when the labels are names rather than periods. */
  orientation?: 'vertical' | 'horizontal' | undefined;
  mode?: 'grouped' | 'stacked' | undefined;
  format?: Formatter | undefined;
  height?: number | undefined;
  margin?: Partial<ChartMargin> | undefined;
  categoryLabel?: string | undefined;
  onSelect?: ((key: string) => void) | undefined;
}

/**
 * Counts and amounts by category.
 *
 * **The value axis always includes zero, and there is no prop to change that.**
 * A bar's length *is* its value — that is the whole encoding — so an axis
 * starting at 40 makes 41 look twice the size of 40. It is the single most
 * common way a chart lies, and it is not a caller's decision to make. A measure
 * that genuinely needs a truncated axis is a measure that wants `LineChart`,
 * where position rather than length carries the value.
 */
export function BarChart({
  title,
  summary,
  series,
  orientation = 'vertical',
  mode = 'grouped',
  format = String,
  height = 240,
  margin,
  categoryLabel = 'Category',
  onSelect,
}: BarChartProps) {
  const keys = useMemo(() => categoryKeys(series), [series]);
  const labels = useMemo(() => categoryLabels(series), [series]);

  // A stack that mixes signs is a bar whose height is neither the total nor the
  // range. Rather than clamp and mislead, fall back to grouped and let the
  // reader see both directions.
  const stacked = mode === 'stacked' && stackIsSound(series);
  const horizontal = orientation === 'horizontal';

  const x = useMemo(() => bandScale(keys, [0, 1], 0.25), [keys]);
  const cursor = useChartCursor(x, 0, onSelect);

  return (
    <ChartFrame
      title={title}
      summary={summary}
      series={series}
      height={height}
      // A horizontal chart spends its width on labels rather than its height.
      margin={horizontal ? { left: 120, bottom: 24, ...margin } : margin}
      format={format}
      categoryLabel={categoryLabel}
      overlay={(plot) =>
        horizontal ? null : (
          <CursorLayer
            cursor={cursor}
            series={series}
            format={format}
            x={bandScale(keys, [0, plot.inner.width], 0.25)}
            width={plot.inner.width}
          />
        )
      }
    >
      {(plot) => (
        <BarPlot
          plot={plot}
          layout={{ keys, labels, series, stacked, horizontal, format, onSelect }}
          cursor={cursor}
        />
      )}
    </ChartFrame>
  );
}

/** What the plot draws, settled before the plot's size is known. */
interface BarLayout {
  keys: readonly string[];
  labels: Map<string, string>;
  series: readonly Series[];
  stacked: boolean;
  horizontal: boolean;
  format: Formatter;
  onSelect?: ((key: string) => void) | undefined;
}

function BarPlot({
  plot,
  layout,
  cursor,
}: {
  plot: PlotBox;
  layout: BarLayout;
  cursor: ChartCursor;
}) {
  const { keys, series, stacked, horizontal, onSelect } = layout;
  const { width, height: inner } = plot.inner;
  const categoryExtent = horizontal ? inner : width;
  const valueExtent = horizontal ? width : inner;

  const category = bandScale(keys, [0, categoryExtent], 0.25);
  const domain = niceDomain(...extentOf(series, { stacked }), tickCountFor(valueExtent, 56));
  // Vertical charts invert — SVG y grows downward — horizontal ones do not.
  const value = linearScale(domain, horizontal ? [0, width] : [inner, 0]);
  const ticks = value.ticks(tickCountFor(valueExtent, 56));

  const stacks = stacked ? stackFor(series, keys) : undefined;
  const zero = value(Math.max(0, domain[0]));

  return (
    <>
      <BarAxes plot={plot} layout={layout} scales={{ category, value, ticks }} />

      {keys.map((key) => (
        <g key={key} aria-hidden="true" className={cursor.key === key ? 'is-active' : undefined}>
          {stacks ? (
            <StackedBar
              segments={stacks.get(key) ?? []}
              layout={layout}
              slot={category(key)}
              band={category.bandwidth}
              value={value}
              onSelect={onSelect ? () => onSelect(key) : undefined}
            />
          ) : (
            <GroupedBars
              categoryKey={key}
              layout={layout}
              slot={category(key)}
              band={category.bandwidth}
              value={value}
              zero={zero}
            />
          )}
        </g>
      ))}

      {!horizontal && (
        <rect
          x={0}
          y={0}
          width={width}
          height={inner}
          fill="transparent"
          onPointerMove={cursor.onPointerMove}
          onPointerLeave={cursor.onPointerLeave}
        />
      )}
    </>
  );
}

interface BarScales {
  category: BandScale;
  value: LinearScale;
  ticks: number[];
}

function BarAxes({
  plot,
  layout,
  scales,
}: {
  plot: PlotBox;
  layout: BarLayout;
  scales: BarScales;
}) {
  const { keys, labels, horizontal, format } = layout;
  const { category, value, ticks } = scales;

  if (horizontal) return <AxisHorizontalLabels category={category} labels={labels} keys={keys} />;

  return (
    <>
      <Gridlines y={value} plot={plot} ticks={ticks} />
      <AxisLeft y={value} ticks={ticks} format={format} />
      <AxisBottom
        x={category}
        plot={plot}
        labels={labels}
        every={labelStride(keys.length, plot.inner.width, longestLabelLength(labels))}
      />
    </>
  );
}

interface BarSlot {
  layout: BarLayout;
  slot: number;
  band: number;
  value: LinearScale;
}

function StackedBar({
  segments,
  layout,
  slot,
  band,
  value,
  onSelect,
}: BarSlot & { segments: readonly StackSegment[]; onSelect?: (() => void) | undefined }) {
  return segments.map((segment) => (
    <Bar
      key={segment.seriesId}
      horizontal={layout.horizontal}
      slot={slot}
      band={band}
      from={value(segment.from)}
      to={value(segment.to)}
      colour={seriesColor(segment.seriesIndex, layout.series[segment.seriesIndex]?.tone)}
      hatch={seriesHatch(segment.seriesIndex)}
      onSelect={segment.datum.href ? onSelect : undefined}
    />
  ));
}

function GroupedBars({
  categoryKey,
  layout,
  slot,
  band,
  value,
  zero,
}: BarSlot & { categoryKey: string; zero: number }) {
  const { series, horizontal, onSelect } = layout;
  const each = band / Math.max(1, series.length);

  return series.map((one, index) => {
    const datum = one.points.find((point) => point.key === categoryKey);
    if (!datum || datum.value === null) return null;
    return (
      <Bar
        key={one.id}
        horizontal={horizontal}
        slot={slot + index * each}
        band={each}
        from={zero}
        to={value(datum.value)}
        colour={seriesColor(index, one.tone)}
        onSelect={onSelect ? () => onSelect(categoryKey) : undefined}
      />
    );
  });
}

interface BarProps {
  horizontal: boolean;
  slot: number;
  band: number;
  from: number;
  to: number;
  colour: string;
  hatch?: string | undefined;
  onSelect?: (() => void) | undefined;
}

function Bar({ horizontal, slot, band, from, to, colour, hatch, onSelect }: BarProps) {
  const geometry = barRect({ horizontal, slot, band, from, to });

  return (
    <>
      <rect
        className="c-chart__bar"
        {...geometry}
        fill={colour}
        onClick={onSelect}
        style={onSelect ? { cursor: 'pointer' } : undefined}
      />
      {/* Hatching from the fourth series on. Hatching everything is noise, and
          the first three are already far enough apart in hue and lightness. */}
      {hatch && <rect {...geometry} fill={`url(#${hatch})`} />}
    </>
  );
}

/** Category labels for a horizontal chart, which reads them along the left. */
function AxisHorizontalLabels({
  category,
  labels,
  keys,
}: {
  category: BandScale;
  labels: Map<string, string>;
  keys: readonly string[];
}) {
  return (
    <g aria-hidden="true">
      {keys.map((key) => (
        <text
          key={key}
          className="c-chart__tick c-chart__tick--y"
          x={-8}
          y={category.centre(key)}
          textAnchor="end"
          dominantBaseline="middle"
        >
          {labels.get(key) ?? key}
        </text>
      ))}
    </g>
  );
}
